import React, { useContext } from "react";
import Ctx from "../../contexts/AppContext";
import { bdg } from "../../constants/styles";
import Ic from "../common/Ic";
import { escHtml } from "../../utils/escHtml";
import { formatMonthLabel, rentPeriod } from "../../utils/dateFormat";

function ReceiptView({ pay, prop, tenant, onBack }) {
  const { t, h, flash } = useContext(Ctx);
  const isAdvanceOnly = pay.amt === 0 && (pay.advance || 0) > 0;
  // Total = everything the tenant actually handed over, INCLUDING any
  // advance credited on top of the rent (₹5,000 rent + ₹1,000 advance
  // must total ₹6,000 — the advance row was listed but not summed).
  const tot = isAdvanceOnly ? pay.advance : pay.amt + (pay.com || 0) + (pay.advance || 0);
  const recNo = `REC-${pay.id.toUpperCase()}-${Date.now().toString().slice(-4)}`;
  const advanceLabel = t.advancePay || "Advance Payment";
  // What the payment was FOR — the receipt previously printed the raw
  // month tag under a misused "Period: 11 months" compliance label, so
  // it never said whether this was rent (and for which month/cycle), a
  // maintenance service, or an advance.
  const isMaint = (pay.mo || "").startsWith("🔧");
  const serviceName = isMaint ? (pay.mo || "").replace("🔧", "").trim() : "";
  const cycle = !isMaint && !isAdvanceOnly ? rentPeriod(pay.mo, tenant?.rent_due_day) : "";
  const paidForVal = isAdvanceOnly
    ? advanceLabel
    : isMaint
      ? `${t.maintenance || "Maintenance"} — ${serviceName}`
      : `${formatMonthLabel(pay.mo)} ${t.rentAmt || "Rent"}`;
  const paidForLbl = t.paidFor || "Paid For";
  const cycleLbl = t.rentPeriodLabel || "Rent Period";
  const receiptTitle = isMaint ? (t.paymentReceipt || "PAYMENT RECEIPT") : t.rentReceipt;
  // Rent receipts state WHEN it was paid; the Rent Period row states what
  // it covered. ("Paid For: May 2026 Rent" contradicted the period row —
  // a May payment covers April's stay under arrears billing.)
  const paidAtLbl = t.paidAt || "Paid At";
  // Paid At with TIME where the data has it. `dt` is stored date-only,
  // but txn ids embed Date.now() at the moment the payment was
  // submitted/approved (tx<ms>, tx_adv_<ms>, tx<ms>_<i>), and advance /
  // maintenance row ids carry one too. Only trusted when it lands on the
  // same day as the recorded paid date — a row id minted when the DUE was
  // generated must not masquerade as the payment time. Legacy rows fall
  // back to the date alone.
  const paidDate = pay.dt || "—";
  const paidAt = (() => {
    if (!pay.dt) return paidDate;
    let best = 0;
    for (const tx of (pay.txns || [])) {
      if (tx.status !== "verified" && tx.status !== "approved") continue;
      const m = /^tx(?:_adv)?_?(\d{13})/.exec(tx.id || "");
      if (m) best = Math.max(best, Number(m[1]));
    }
    if (!best) {
      const m2 = /_(\d{13})(?:_|$)/.exec(pay.id || "");
      if (m2) best = Number(m2[1]);
    }
    if (!best) return paidDate;
    const d = new Date(best);
    const sameDay = d.toISOString().slice(0, 10) === pay.dt || d.toLocaleDateString("en-CA") === pay.dt;
    if (!sameDay) return paidDate;
    return `${pay.dt}, ${d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" })}`;
  })();
  const baseAmt = pay.amt + (pay.com || 0);
  const amountLines = isAdvanceOnly
    ? `${advanceLabel}: ₹${pay.advance.toLocaleString()}`
    : isMaint
      ? `${t.serviceCharge || "Service Charge"}: ₹${baseAmt.toLocaleString()}${(pay.advance || 0) > 0 ? `\n${advanceLabel}: ₹${pay.advance.toLocaleString()}` : ""}\n${t.total}: ₹${tot.toLocaleString()}`
      : `${t.rentAmount}: ₹${pay.amt.toLocaleString()}\n${t.commonCharges}: ₹${(pay.com || 0).toLocaleString()}${(pay.advance || 0) > 0 ? `\n${advanceLabel}: ₹${pay.advance.toLocaleString()}` : ""}\n${t.total}: ₹${tot.toLocaleString()}`;
  const receiptText = `${receiptTitle}\n${t.app}\n${"─".repeat(30)}\n${t.receiptNo}: ${recNo}\n${t.date}: ${pay.dt || new Date().toISOString().slice(0, 10)}\n\n${t.property}: ${prop?.addr || "—"}\n${t.tenant}: ${tenant?.name || "—"}\n${t.phone}: ${tenant?.ph || "—"}\n\n${isMaint ? `${paidForLbl}: ${paidForVal}\n` : ""}${paidAtLbl}: ${paidAt}${cycle ? `\n${cycleLbl}: ${cycle}` : ""}\n${amountLines}\n\n${t.method}: ${pay.mth?.toUpperCase() || "—"}\n${t.status}: ${pay.st?.toUpperCase() || "—"}\n${"─".repeat(30)}\nGenerated by ProRentz`;

  const handleDownload = () => {
    try { const blob = new Blob([receiptText], { type: "text/plain" }); const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `Receipt_${pay.mo.replace(/\s/g, "_")}_${tenant?.name?.replace(/\s/g, "_") || "tenant"}.txt`; a.click(); URL.revokeObjectURL(url); flash(t.receiptDl); }
    catch (e) { flash(t.dlStarted); }
  };

  const handlePrintPDF = () => {
    // SECURITY: open with noopener so the popup can't tamper with this window
    // (reverse tabnabbing) and so it has no reference to localStorage in
    // window.opener.
    const win = window.open("", "_blank", "noopener,noreferrer,width=480,height=700");
    if (!win) { flash("Allow popups to print receipt", "error"); return; }
    // SECURITY: escape every interpolated value below so a tenant who put a
    // <script> in their name can't run it inside the same-origin popup and
    // exfiltrate localStorage.auth_token.
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Receipt - ${escHtml(paidForVal)}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1a1a2e; padding: 32px 28px; max-width: 480px; margin: 0 auto; }
  .header { text-align: center; padding-bottom: 20px; border-bottom: 2px solid #4f46e5; margin-bottom: 20px; }
  .logo { width: 44px; height: 44px; background: #4f46e5; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 8px; }
  .app-name { font-size: 18px; font-weight: 700; color: #4f46e5; }
  .receipt-title { font-size: 12px; color: #6b7280; margin-top: 2px; text-transform: uppercase; letter-spacing: 1px; }
  .rec-meta { display: flex; justify-content: space-between; background: #f5f3ff; border-radius: 8px; padding: 12px 14px; margin-bottom: 18px; font-size: 12px; }
  .rec-meta span { color: #6b7280; }
  .rec-meta strong { color: #1a1a2e; font-weight: 600; }
  .section-label { font-size: 10px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.8px; margin-bottom: 6px; }
  .section { border-bottom: 1px dashed #e5e7eb; padding-bottom: 14px; margin-bottom: 14px; }
  .row { display: flex; justify-content: space-between; padding: 5px 0; font-size: 13px; }
  .row .lbl { color: #6b7280; }
  .row .val { color: #1a1a2e; font-weight: 500; }
  .total-row { display: flex; justify-content: space-between; padding: 12px 0 0; border-top: 2px solid #4f46e5; margin-top: 4px; }
  .total-row .lbl { font-size: 14px; font-weight: 700; color: #1a1a2e; }
  .total-row .val { font-size: 20px; font-weight: 700; color: #4f46e5; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: 600; ${pay.st === "paid" ? "background: #d1fae5; color: #065f46;" : "background: #fee2e2; color: #991b1b;"} }
  .footer { text-align: center; margin-top: 24px; font-size: 10px; color: #9ca3af; }
  @media print { body { padding: 20px; } button { display: none !important; } }
</style></head><body>
<div class="header">
  <div class="logo"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2"><path d="M4 2h16a2 2 0 012 2v16a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2zM9 22v-4h6v4M8 6h.01M16 6h.01M12 10h.01M8 14h.01M16 14h.01"/></svg></div>
  <div class="app-name">ProRentz</div>
  <div class="receipt-title">${escHtml(receiptTitle || "Rent Receipt")}</div>
</div>
<div class="rec-meta">
  <div><span>Receipt No</span><br/><strong>${escHtml(recNo)}</strong></div>
  <div style="text-align:right"><span>Date</span><br/><strong>${escHtml(pay.dt || new Date().toISOString().slice(0, 10))}</strong></div>
</div>
<div class="section">
  <div class="section-label">Property</div>
  <div style="font-size:14px;font-weight:600;color:#1a1a2e">${escHtml(prop?.addr || "—")}</div>
</div>
<div class="section">
  <div class="section-label">Tenant</div>
  <div style="font-size:14px;font-weight:600;color:#1a1a2e">${escHtml(tenant?.name || "—")}</div>
  <div style="font-size:12px;color:#6b7280;margin-top:2px">${escHtml(tenant?.ph || "")}</div>
</div>
<div class="section">
  <div class="section-label">Payment Details</div>
  ${isAdvanceOnly ? `<div class="row"><span class="lbl">Advance Payment</span><span class="val">₹${escHtml(pay.advance.toLocaleString())}</span></div>` : isMaint ? `<div class="row"><span class="lbl">${escHtml(t.serviceCharge || "Service Charge")}</span><span class="val">₹${escHtml(baseAmt.toLocaleString())}</span></div>
  ${(pay.advance || 0) > 0 ? `<div class="row"><span class="lbl">Advance</span><span class="val">₹${escHtml(pay.advance.toLocaleString())}</span></div>` : ""}` : `<div class="row"><span class="lbl">Rent Amount</span><span class="val">₹${escHtml(pay.amt.toLocaleString())}</span></div>
  ${pay.com > 0 ? `<div class="row"><span class="lbl">Common Charges</span><span class="val">₹${escHtml((pay.com).toLocaleString())}</span></div>` : ""}
  ${(pay.advance || 0) > 0 ? `<div class="row"><span class="lbl">Advance</span><span class="val">₹${escHtml(pay.advance.toLocaleString())}</span></div>` : ""}`}
  ${isMaint ? `<div class="row"><span class="lbl">${escHtml(paidForLbl)}</span><span class="val">${escHtml(paidForVal)}</span></div>` : ""}
  <div class="row"><span class="lbl">${escHtml(paidAtLbl)}</span><span class="val">${escHtml(paidAt)}</span></div>
  ${cycle ? `<div class="row"><span class="lbl">${escHtml(cycleLbl)}</span><span class="val">${escHtml(cycle)}</span></div>` : ""}
  <div class="row"><span class="lbl">Payment Method</span><span class="val">${escHtml((pay.mth || "—").toUpperCase())}</span></div>
  <div class="total-row"><span class="lbl">Total Paid</span><span class="val">₹${escHtml(tot.toLocaleString())}</span></div>
</div>
<div style="text-align:center;padding:8px 0">
  <span class="status-badge">${pay.st === "paid" ? "✓ PAID" : "⏳ DUE"}</span>
</div>
<div class="footer">Generated by ProRentz</div>
<br/>
<button onclick="window.print()" style="width:100%;padding:12px;background:#4f46e5;color:#fff;border:none;border-radius:10px;font-size:14px;font-weight:600;cursor:pointer;margin-top:8px">🖨️ Print / Save as PDF</button>
</body></html>`);
    win.document.close();
    win.focus();
    flash(t.receiptDl || "Receipt ready — print or save as PDF");
  };
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: `Rent Receipt - ${pay.mo}`, text: receiptText });
      } catch (err) {
        // Share cancelled or failed, try clipboard as fallback if not cancelled
        if (err.name !== 'AbortError') {
          try { await navigator.clipboard.writeText(receiptText); flash(t.clipCopied); } catch (e) { flash(t.shareFail, "error"); }
        }
      }
    } else {
      try {
        await navigator.clipboard.writeText(receiptText);
        flash(t.clipCopied);
      } catch (e) {
        // Fallback for non-secure contexts where clipboard API might be blocked
        const textArea = document.createElement("textarea");
        textArea.value = receiptText;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          flash(t.clipCopied);
        } catch (err) {
          flash(t.shareFail, "error");
        }
        document.body.removeChild(textArea);
      }
    }
  };

  return <div>
    <button onClick={onBack} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", fontSize: 13, marginBottom: 12 }}>&larr; {t.back}</button>
    <div style={{ background: h.cd, border: `2px solid ${h.ac}`, borderRadius: 16, padding: 20, marginBottom: 16 }}>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, background: h.ac, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 8px" }}><Ic n="bldg" s={24} c="#fff" /></div>
        <div style={{ color: h.ac, fontWeight: 700, fontSize: 16 }}>{t.app}</div>
        <div style={{ color: h.sc, fontSize: 11 }}>{receiptTitle}</div>
      </div>
      <div style={{ background: `${h.ac}08`, borderRadius: 10, padding: 12, marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}><span style={{ color: h.sc, fontSize: 11 }}>{t.receiptNo}</span><span style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{recNo}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: h.sc, fontSize: 11 }}>{t.date}</span><span style={{ color: h.tx, fontSize: 11, fontWeight: 600 }}>{pay.dt || "—"}</span></div>
      </div>
      <div style={{ borderBottom: `1px dashed ${h.bd}`, paddingBottom: 12, marginBottom: 12 }}>
        <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>{t.property}</div>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{prop?.addr || "—"}</div>
      </div>
      <div style={{ borderBottom: `1px dashed ${h.bd}`, paddingBottom: 12, marginBottom: 12 }}>
        <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>{t.tenant}</div>
        <div style={{ color: h.tx, fontSize: 13, fontWeight: 600 }}>{tenant?.name || "—"}</div>
        <div style={{ color: h.sc, fontSize: 11 }}>{tenant?.ph || "—"}</div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ color: h.sc, fontSize: 10, fontWeight: 600, marginBottom: 8, textTransform: "uppercase" }}>{t.payDetails}</div>
        {(isAdvanceOnly
          ? [[advanceLabel, `₹${pay.advance.toLocaleString()}`], [paidAtLbl, paidAt], [t.method, pay.mth?.toUpperCase() || "—"]]
          : isMaint
            ? [[t.serviceCharge || "Service Charge", `₹${baseAmt.toLocaleString()}`], ...((pay.advance || 0) > 0 ? [[advanceLabel, `₹${pay.advance.toLocaleString()}`]] : []), [paidForLbl, paidForVal], [paidAtLbl, paidAt], [t.method, pay.mth?.toUpperCase() || "—"]]
            : [[t.rentAmount, `₹${pay.amt.toLocaleString()}`], [t.commonCharges, `₹${(pay.com || 0).toLocaleString()}`], ...((pay.advance || 0) > 0 ? [[advanceLabel, `₹${pay.advance.toLocaleString()}`]] : []), [paidAtLbl, paidAt], ...(cycle ? [[cycleLbl, cycle]] : []), [t.method, pay.mth?.toUpperCase() || "—"]]
        ).map(([k, v], i) =>
          <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
            <span style={{ color: h.sc, fontSize: 12 }}>{k}</span><span style={{ color: h.tx, fontSize: 12, fontWeight: 500 }}>{v}</span></div>)}
        <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", borderTop: `2px solid ${h.ac}`, marginTop: 8 }}>
          <span style={{ color: h.tx, fontSize: 14, fontWeight: 700 }}>{t.total}</span><span style={{ color: h.ac, fontSize: 18, fontWeight: 700 }}>₹{tot.toLocaleString()}</span></div>
      </div>
      <div style={{ textAlign: "center", padding: "8px 0", borderTop: `1px dashed ${h.bd}` }}>
        <span style={bdg(pay.st === "paid" ? `${h.gn}20` : `${h.rd}20`, pay.st === "paid" ? h.gn : h.rd)}>{pay.st === "paid" ? "✓ " + t.paidStatus : "⏳ " + t.dueStatus}</span>
      </div>
      <div style={{ textAlign: "center", marginTop: 12, color: h.sc, fontSize: 10 }}>Generated by {t.app || "ProRentz"}</div>
    </div>
    <div style={{ display: "flex", gap: 8 }}>
      <button onClick={handlePrintPDF} style={{ flex: 2, padding: 12, borderRadius: 12, border: "none", background: h.ac, color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{"🖨️ " + (t.printPdf || "Print / PDF")}</button>
      <button onClick={handleShare} style={{ flex: 1, padding: 12, borderRadius: 12, border: `2px solid ${h.ac}`, background: "transparent", color: h.ac, cursor: "pointer", fontSize: 13, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>{"📤 " + t.share}</button>
      <button onClick={handleDownload} style={{ padding: 12, borderRadius: 12, border: `1px solid ${h.bd}`, background: h.ib, color: h.ts, cursor: "pointer", fontSize: 13 }}>{"📥"}</button>
    </div>
  </div>;
}

export default ReceiptView;
